import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form } from 'react-bootstrap';
import moment from 'moment';
import config from '../../services/apiConfig';
import { apiPaths } from '../../services/apiPath';
import AdminNavBar from '../../components/UI/AdminNavBar';
import { getFromLocalStorage, deleteFromLocalStorage } from '../../store';
import Footer from '../../components/UI/Footer';

const VendorScheduleReviewPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commentModal, setCommentModal] = useState(false);
  const [comment, setComment] = useState("");
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);

  const [isSuperAdminLoggedIn, setIsSuperAdminLoggedIn] = useState(getFromLocalStorage("isSuperAdminLoggedIn") || "false");
  const [isAccountantLoggedIn, setIsAccountantLoggedIn] = useState(getFromLocalStorage("isAccountantLoggedIn") || "false");
  const [isStockAdminLoggedIn, setIsStockAdminLoggedIn] = useState(getFromLocalStorage("isStockAdminLoggedIn") || "false");
  const [isEventCoordinatorLoggedIn, setIsEventCoordinatorLoggedIn] = useState(getFromLocalStorage("isEventCoordinatorLoggedIn") || "false");

  useEffect(() => {
    if (isSuperAdminLoggedIn !== "true" && isEventCoordinatorLoggedIn !== "true") {
      window.location.href = "/admin";
    } else {
      fetchSchedules();
    }
  }, []);

  const logout = () => {
    deleteFromLocalStorage("isSuperAdminLoggedIn");
    deleteFromLocalStorage("isEventCoordinatorLoggedIn");
    deleteFromLocalStorage("isAccountantLoggedIn");
    deleteFromLocalStorage("isStockAdminLoggedIn");
    setIsSuperAdminLoggedIn("false");
    setIsEventCoordinatorLoggedIn("false");
    setIsAccountantLoggedIn("false");
    setIsStockAdminLoggedIn("false");
    window.location.reload();
  };

  const fetchSchedules = async () => {
    setLoading(true);
  
    const urlParams = new URLSearchParams(window.location.search);
    const vendorIdFromUrl = urlParams.get("vendor_id");
    const weekFromUrl = urlParams.get("week");
    const startDateFromUrl = urlParams.get("start_date");
  
    let startDate = moment().startOf('week');
    let endDate = moment().endOf('week');
  
    if (weekFromUrl && startDateFromUrl) {
      const year = moment(startDateFromUrl).year();
      startDate = moment().isoWeekYear(year).isoWeek(parseInt(weekFromUrl)).startOf('isoWeek');
      endDate = moment().isoWeekYear(year).isoWeek(parseInt(weekFromUrl)).endOf('isoWeek');
    }
  
    const formattedStartDate = startDate.format("YYYY-MM-DD");
    const formattedEndDate = endDate.format("YYYY-MM-DD");
  
    try {
      const response = await fetch(config.BASE_URI + apiPaths.adminData, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload: {
            body: {
              query_type: 'fetch_vendor_schedules',
              start_date: formattedStartDate,
              end_date: formattedEndDate,
              vendor_id: vendorIdFromUrl ? parseInt(vendorIdFromUrl) : null
            }
          }
        })
      });
  
      const data = await response.json();
      if (data.success) {
        setSchedules(data.schedules);
      } else {
        console.warn("⚠️ No schedules returned.");
      }
    } catch (err) {
      console.error("❌ Error fetching schedules:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, comments = "") => {
    try {
      await fetch(config.BASE_URI + apiPaths.adminData, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload: {
            body: {
              query_type: 'update_vendor_schedule_status',
              schedule_id: id,
              status: status,
              approved_by: "Admin User",
              comments: comments
            }
          }
        })
      });

      fetchSchedules();
      setComment("");
      setCommentModal(false);
    } catch (err) {
      console.error("❌ Error updating status:", err);
    }
  };

  const handleRevise = (id) => {
    setSelectedScheduleId(id);
    setCommentModal(true);
  };

  return (
    <div className="admin-layout">
      <AdminNavBar
        isSuperAdminLoggedIn={isSuperAdminLoggedIn}
        isEventCoordinatorLoggedIn={isEventCoordinatorLoggedIn}
        isAccountantLoggedIn={isAccountantLoggedIn}
        isStockAdminLoggedIn={isStockAdminLoggedIn}
        logout={logout}
      />

      <div className="container py-5">
        <h2 className="fw-bold mb-4">Vendor Schedule Draft Review</h2>
        {loading && <p>Loading schedules...</p>}
        {!loading && schedules.length === 0 && <p>No draft schedules found.</p>}
        {!loading && schedules.map(schedule => (
          <Card key={schedule.id} className="mb-3 shadow-sm">
            <Card.Body>
              <Card.Title>{schedule.vendor_name}</Card.Title>
              <Card.Subtitle className="mb-2 text-muted">
                Week of {schedule.week_scheduled_for}
              </Card.Subtitle>
              <Card.Text>
                <strong>Status:</strong> {schedule.status}<br />
                <strong>Created At:</strong> {schedule.application_scheduled_date_time}<br />
                <strong>Suggestions:</strong> {schedule.wtt_suggestions || "None"}
              </Card.Text>
              <Button
                variant="success"
                className="me-2"
                onClick={() => updateStatus(schedule.id, "Accepted")}
              >
                 Accept
              </Button>
              <Button
                variant="danger"
                className="me-2"
                onClick={() => updateStatus(schedule.id, "Rejected")}
              >
                 Reject
              </Button>
              <Button
                variant="warning"
                onClick={() => handleRevise(schedule.id)}
              >
                 Ask to Revise
              </Button>
            </Card.Body>
          </Card>
        ))}

        <Modal show={commentModal} onHide={() => setCommentModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Ask to Revise</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Revision Comments</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Write your feedback here..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setCommentModal(false)}>
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={() => updateStatus(selectedScheduleId, "Awaiting Approval", comment)}
            >
              Send Revision Request
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <Footer />
    </div>
  );
};

export default VendorScheduleReviewPage;
